import type { SuperHubHashRouteParams } from '@routes/SuperHub/types';
import type { Location } from 'history';
import type { useHistory } from 'react-router-dom';

import { useEffect } from 'react';
import { matchPath } from 'react-router-dom';

import { SuperHubHashRoutePaths } from '@routes/SuperHub/types';

/**
 * Hash actions whose overlays SHOULD dismiss when the pathname changes
 * (e.g. settings panels). All other matched hash actions are persistent
 * overlays that survive pathname changes (`#/appearance/...`).
 */
const TRANSIENT_HASH_ACTIONS = new Set(['branches', 'settings', 'docs-audit', 'my-developers']);

/** Returns the parent hash if it currently represents a persistent overlay; else null. */
function persistentHash(hash: string): string | null {
  if (!hash) return null;
  const match = matchPath<SuperHubHashRouteParams>(hash.substring(1), Object.values(SuperHubHashRoutePaths));
  if (!match || (match.params.action && TRANSIENT_HASH_ACTIONS.has(match.params.action))) return null;
  return hash;
}

interface PatchedHistory {
  __readmeHashPreservePatched?: boolean;
  push: (location: string | Partial<Location>, state?: unknown) => void;
  replace: (location: string | Partial<Location>, state?: unknown) => void;
}

/**
 * Patches the SuperHub `BrowserHistory`'s `push`/`replace` once so that pushing
 * a new pathname **without** a hash auto-preserves any persistent hash overlay
 * currently shown (e.g. `#/appearance/...`).
 *
 * ### Why this is necessary
 * The docs preview iframe shares the parent's history (`crossFrameHistory`),
 * and `<Link>`s inside that iframe — including links rendered by SuperHub
 * itself when the docs preview runs inline — push pathname-only locations.
 * They have no awareness of the parent's hash overlays. Without this patch,
 * such pushes drop the hash, briefly unmounting the overlay panel before
 * `ConnectPathHash`'s store-level `useEffect` can restore it on the next tick.
 * That round-trip is visible as a flash.
 *
 * ### Why this is implemented as a method patch
 * - The unpatched history reference is captured by react-router's
 *   `<BrowserRouter>` at top-level mount, so wrapping the global with a
 *   `Proxy` after-the-fact misses calls that go through the captured ref.
 * - The push originates inside generic `<Link>` components that we can't
 *   modify (no API surface to "ask" them to preserve a hash).
 * - Wrapping at `crossFrameHistory` would leak SuperHub-specific overlay
 *   knowledge into a utility shared with the legacy Hub.
 *
 * Patching the methods directly is the smallest contained workaround. Calls
 * with an explicit hash (including `'#/'` for explicit dismissal) pass
 * through unchanged.
 */
export default function usePersistHashOverlays(history: ReturnType<typeof useHistory>) {
  useEffect(() => {
    const h = history as unknown as PatchedHistory;
    if (h.__readmeHashPreservePatched) return;
    if (typeof h.push !== 'function' || typeof h.replace !== 'function') return;
    h.__readmeHashPreservePatched = true;

    const wrap = (original: PatchedHistory['push']) => (location: string | Partial<Location>, state?: unknown) => {
      const hashToPreserve = persistentHash(window.location.hash);
      if (hashToPreserve) {
        if (typeof location === 'string' && !location.includes('#')) {
          return original.call(h, `${location}${hashToPreserve}`, state);
        }
        if (location && typeof location === 'object' && !location.hash) {
          return original.call(h, { ...location, hash: hashToPreserve }, state);
        }
      }
      return original.call(h, location, state);
    };

    h.push = wrap(h.push.bind(h));
    h.replace = wrap(h.replace.bind(h));
  }, [history]);
}
